package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.ThemeColors
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Types of celebrations that can be displayed
 */
enum class CelebrationType(
    val emoji: String,
    val title: String,
    val subtitle: String,
    val backgroundColor: String,
    val accentColor: String,
    val showConfetti: Boolean = false
) {
    ACE(
        emoji = "\uD83C\uDFBE",
        title = "ACE!",
        subtitle = "+3 points",
        backgroundColor = "rgba(33, 150, 243, 0.95)",
        accentColor = "#64B5F6"
    ),
    BREAK_CONVERTED(
        emoji = "\uD83D\uDD25",
        title = "BREAK!",
        subtitle = "+4 points",
        backgroundColor = "rgba(255, 152, 0, 0.95)",
        accentColor = "#FFB74D"
    ),
    MATCH_WIN(
        emoji = "\uD83C\uDFC6",
        title = "MATCH WON!",
        subtitle = "Victory bonus",
        backgroundColor = "rgba(76, 175, 80, 0.95)",
        accentColor = "#81C784",
        showConfetti = true
    ),
    ENTERED_PAYOUT(
        emoji = "\uD83D\uDCB0",
        title = "IN THE MONEY!",
        subtitle = "You've entered the payout zone",
        backgroundColor = "rgba(76, 175, 80, 0.95)",
        accentColor = "#A5D6A7",
        showConfetti = true
    ),
    RANK_UP(
        emoji = "\uD83D\uDE80",
        title = "MOVING UP!",
        subtitle = "",
        backgroundColor = "rgba(103, 58, 183, 0.95)",
        accentColor = "#B39DDB"
    ),
    PASSED_OPPONENT(
        emoji = "\u26A1",
        title = "PASSED!",
        subtitle = "",
        backgroundColor = "rgba(0, 150, 136, 0.95)",
        accentColor = "#80CBC4"
    )
}

/**
 * Data class for celebration events
 */
data class CelebrationEvent(
    val type: CelebrationType,
    val playerName: String? = null,
    val customSubtitle: String? = null,
    val id: String = kotlin.random.Random.nextInt().toString()
)

/**
 * Full-screen celebration overlay for major events
 *
 * Features:
 * - Animated entrance/exit
 * - Confetti for big moments
 * - Auto-dismiss after duration
 * - Manual dismiss on tap
 */
@Composable
fun CelebrationOverlay(
    event: CelebrationEvent,
    onDismiss: () -> Unit,
    durationMs: Long = 2500
) {
    var isVisible by remember { mutableStateOf(false) }
    var isExiting by remember { mutableStateOf(false) }

    // Animate in on mount
    LaunchedEffect(event.id) {
        isVisible = true
        delay(durationMs)
        isExiting = true
        delay(300) // Exit animation duration
        onDismiss()
    }

    // Full screen overlay
    Div({
        classes("celebration-overlay")
        if (isVisible && !isExiting) classes("celebration-overlay-visible")
        if (isExiting) classes("celebration-overlay-exiting")
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            backgroundColor(Color(event.type.backgroundColor))
            property("z-index", "9999")
            cursor("pointer")
            property("backdrop-filter", "blur(4px)")
            opacity(if (isVisible && !isExiting) 1 else 0)
            property("transition", "opacity 0.3s ease")
        }
        onClick {
            isExiting = true
        }
    }) {
        // Confetti layer
        if (event.type.showConfetti) {
            ConfettiLayer()
        }

        // Main content
        Div({
            classes("celebration-content")
            style {
                textAlign("center")
                property("transform", if (isVisible && !isExiting) "scale(1)" else "scale(0.8)")
                property("transition", "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)")
            }
        }) {
            // Emoji with bounce animation
            Div({
                classes("celebration-emoji")
                style {
                    fontSize(80.px)
                    marginBottom(16.px)
                    property("animation", "celebrationBounce 0.6s ease-out")
                }
            }) {
                Text(event.type.emoji)
            }

            // Title
            H1({
                style {
                    fontSize(48.px)
                    fontWeight(900)
                    color(Color.white)
                    margin(0.px)
                    marginBottom(8.px)
                    property("text-shadow", "0 4px 20px rgba(0,0,0,0.3)")
                    letterSpacing(2.px)
                    property("animation", "celebrationSlideUp 0.4s ease-out 0.1s both")
                }
            }) {
                Text(event.type.title)
            }

            // Player name if provided
            event.playerName?.let { name ->
                Div({
                    style {
                        fontSize(24.px)
                        fontWeight(600)
                        color(Color(event.type.accentColor))
                        marginBottom(8.px)
                        property("animation", "celebrationSlideUp 0.4s ease-out 0.2s both")
                    }
                }) {
                    Text(name)
                }
            }

            // Subtitle
            val subtitle = event.customSubtitle ?: event.type.subtitle
            if (subtitle.isNotEmpty()) {
                Div({
                    style {
                        fontSize(20.px)
                        color(Color("rgba(255,255,255,0.9)"))
                        fontWeight(500)
                        property("animation", "celebrationSlideUp 0.4s ease-out 0.3s both")
                    }
                }) {
                    Text(subtitle)
                }
            }
        }

        // Tap to dismiss hint
        Div({
            style {
                position(Position.Absolute)
                bottom(40.px)
                fontSize(14.px)
                color(Color("rgba(255,255,255,0.6)"))
                property("animation", "celebrationFadeIn 0.5s ease-out 0.5s both")
            }
        }) {
            Text("Tap to dismiss")
        }
    }
}

/**
 * Confetti particle layer
 */
@Composable
private fun ConfettiLayer() {
    val colors = listOf("#FFD700", "#FF6B6B", "#4CAF50", "#2196F3", "#E91E63", "#9C27B0")

    Div({
        style {
            position(Position.Absolute)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            overflow("hidden")
            property("pointer-events", "none")
        }
    }) {
        // Generate confetti pieces
        repeat(50) { index ->
            val color = colors[index % colors.size]
            val leftPercent = (index * 2) % 100
            val delay = (index * 50) % 1000
            val duration = 2000 + (index * 30) % 1000

            Div({
                style {
                    position(Position.Absolute)
                    left(leftPercent.percent)
                    top((-20).px)
                    width(10.px)
                    height(10.px)
                    backgroundColor(Color(color))
                    borderRadius(if (index % 3 == 0) 50.percent else 2.px)
                    property("animation", "confettiFall ${duration}ms linear ${delay}ms infinite")
                    property("transform", "rotate(${index * 30}deg)")
                }
            })
        }
    }
}

/**
 * Toast-style celebration for less intrusive notifications
 */
@Composable
fun CelebrationToast(
    event: CelebrationEvent,
    onDismiss: () -> Unit,
    @Suppress("UNUSED_PARAMETER") colors: ThemeColors, // Reserved for future theming
    durationMs: Long = 3000
) {
    var isVisible by remember { mutableStateOf(false) }
    var isExiting by remember { mutableStateOf(false) }

    LaunchedEffect(event.id) {
        isVisible = true
        delay(durationMs)
        isExiting = true
        delay(300)
        onDismiss()
    }

    Div({
        classes("celebration-toast")
        if (isVisible && !isExiting) classes("celebration-toast-visible")
        style {
            position(Position.Fixed)
            top(80.px)
            left(50.percent)
            property("transform", "translateX(-50%) ${if (isVisible && !isExiting) "translateY(0)" else "translateY(-20px)"}")
            backgroundColor(Color(event.type.backgroundColor))
            borderRadius(12.px)
            padding(12.px, 20.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(12.px)
            property("z-index", "9998")
            property("box-shadow", "0 4px 20px rgba(0,0,0,0.3)")
            opacity(if (isVisible && !isExiting) 1 else 0)
            property("transition", "all 0.3s ease")
            cursor("pointer")
        }
        onClick { isExiting = true }
    }) {
        // Emoji
        Span({ style { fontSize(24.px) } }) {
            Text(event.type.emoji)
        }

        // Content
        Div {
            Div({
                style {
                    fontWeight(700)
                    color(Color.white)
                    fontSize(14.px)
                }
            }) {
                Text(event.type.title)
                event.playerName?.let { name ->
                    Text(" - $name")
                }
            }
            val subtitle = event.customSubtitle ?: event.type.subtitle
            if (subtitle.isNotEmpty()) {
                Div({
                    style {
                        fontSize(12.px)
                        color(Color("rgba(255,255,255,0.8)"))
                    }
                }) {
                    Text(subtitle)
                }
            }
        }
    }
}

/**
 * Manager for celebration queue - shows celebrations one at a time
 */
@Composable
fun CelebrationManager(
    celebrations: List<CelebrationEvent>,
    onCelebrationDismissed: (String) -> Unit,
    colors: ThemeColors,
    useFullScreen: Boolean = true
) {
    // Only show the first celebration in the queue
    val currentCelebration = celebrations.firstOrNull()

    currentCelebration?.let { event ->
        if (useFullScreen && event.type.showConfetti) {
            CelebrationOverlay(
                event = event,
                onDismiss = { onCelebrationDismissed(event.id) }
            )
        } else {
            CelebrationToast(
                event = event,
                onDismiss = { onCelebrationDismissed(event.id) },
                colors = colors
            )
        }
    }
}
